import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SaveIcon from '@mui/icons-material/Save';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Accordion, AccordionDetails, AccordionSummary, Button, Chip, FormControlLabel, Grid, IconButton, Switch, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material';
import { useSnackbar } from 'notistack';
import React from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { styled } from '@mui/material/styles';

import { CREATE, DataAPIContext, DELETE, GET_LIST, UPDATE } from '../api';
import LocalizedTextSetField from '../components/LocalizedTextField';
import { REVIEW_ASPECT_COMMENT_TYPE_ALWAYS, REVIEW_ASPECT_COMMENT_TYPE_HIGH_RATING, REVIEW_ASPECT_COMMENT_TYPE_LOW_RATING, REVIEW_ASPECT_TYPE_COMMENT, REVIEW_ASPECT_TYPE_RATING } from '../settings/utils';
import { useIsWidthUp } from '../utils';
import DeleteIconButton from '../components/DeleteIconButton';
import FixedLinearProgress from '../components/FixedLinearProgress';
import { ContextStore } from '../store/contextStore';
import useConfirmDialog from '../components/useConfirmDialog';

const sortBySequence = (a, b) => +a.sequence - +b.sequence;

const getComparableState = (page) => {
    return JSON.stringify(page);
};

const DraggableAspect = ({ aspect, index, moveAspect, ...props }) => {
    const ref = React.useRef(null);
    const isSmallScreen = !useIsWidthUp('sm');


    const [, drag] = useDrag({
        type: 'aspect',
        item: { index },
        canDrag: !isSmallScreen,
    });

    const [, drop] = useDrop({
        accept: 'aspect',
        hover: (draggedItem, monitor) => {
            if (isSmallScreen || !ref.current) {
                return;
            }
            const dragIndex = draggedItem.index;
            const hoverIndex = index;
            if (dragIndex === hoverIndex) {
                return;
            }
            const hoverBoundingRect = ref.current.getBoundingClientRect();
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            const clientOffset = monitor.getClientOffset();
            const hoverClientY = clientOffset.y - hoverBoundingRect.top;

            // Dragging downwards
            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
                return;
            }

            // Dragging upwards
            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
                return;
            }

            moveAspect(dragIndex, hoverIndex);
            draggedItem.index = hoverIndex;
        },
    });

    drag(drop(ref));

    return (
        <div ref={ref} style={{ width: '100%' }}>
            {props.children}
        </div>
    );
};

const ReviewWidgetPage = (props) => {
    const [confirmDelete, confirmDeleteDialog] = useConfirmDialog();
    const theme = useTheme();
    const isSmallScreen = !useIsWidthUp('sm');
    const isCommentPage = props.page.name === "Comment page";
    const disabled = props.disabled;

    const updateAspect = (aspect, idx) => {
        const page = props.page;
        page.aspects = page.aspects.map((x, i) => i === idx ? aspect : x);
        props.onUpdatePage(page);
    }

    const onAspectTextChange = (ev, aspect, idx) => {
        aspect[ev.target.name] = ev.target.value;
        updateAspect(aspect, idx);
    }

    // CP-5220: hide an aspect from guests without deleting it (and its translations); the review
    // page only offers enabled aspects
    const onAspectSwitchChange = (ev, aspect, idx) => {
        aspect.enabled = !!ev.target.checked;
        updateAspect(aspect, idx);
    }

    const onDeleteAspect = async (ev, aspect, idx) => {
        if (await confirmDelete({ body: `Are you sure you want to delete aspect? (${idx})` })) {
            const page = props.page;
            page.aspects = page.aspects.filter((x, i) => i !== idx);
            props.onUpdatePage(page);
        }
    }

    const moveAspect = (dragIndex, hoverIndex) => {
        const draggedAspect = props.page.aspects[dragIndex];
        const newAspects = [...props.page.aspects];
        newAspects.splice(dragIndex, 1);
        newAspects.splice(hoverIndex, 0, draggedAspect);
        
        newAspects.forEach((aspect, index) => {
            aspect.sequence = index + 1;
        });

        props.onUpdatePage({
            ...props.page,
            aspects: newAspects
        });
    };
    const getPageNumber = (page) => {
        if(page.name === "Thank you page") {
            return 3;
        }
        if(isCommentPage) {
            return 2;
        }
        return 1;
    }

    return (<Grid container direction="row" spacing={2} >
        {confirmDeleteDialog}
        <Grid item xs={12} sm={7} container alignItems="center" direction="row" spacing={1}>
            <Grid item xs={7} sm={7}>
                <Typography fontWeight={500} fontSize={16}>{getPageNumber(props.page)}. {props.page.name}</Typography>
            </Grid>
        </Grid>
        <Grid item xs={12} sm={5} container alignItems="center" justifyContent="flex-end">
          {props.page.name !== "Thank you page" &&    <FormControlLabel
                disabled={disabled}
                control={
                    <Switch name="enabled" checked={props.page.enabled} onChange={(ev) => props.onSwitchChange(ev, props.page)} />
                }
                label="Show page"
            />}
           <Tooltip title="Save">
                <span>
                    <IconButton disabled={disabled || !props.pageChanged } onClick={(ev) => props.onSave(ev, props.page)} >
                        <SaveIcon color={ props.pageChanged ? "primary" : "" }/>
                    </IconButton>
                </span>
            </Tooltip>
        </Grid>
        <Grid item xs={12} container direction="row" spacing={2} sx={{ marginLeft: theme.spacing(2) }}>
            <Grid item xs={12}>
                <LocalizedTextSetField disabled={disabled} variant="filled" size="small" label="Title" name="title" value={props.page.title} fullWidth onChange={(ev) => props.onTextChange(ev, props.page)} />
            </Grid>
            <Grid item xs={12}>
                <LocalizedTextSetField disabled={disabled} variant="filled" size="small" label="Body" name="body" value={props.page.body} fullWidth onChange={(ev) => props.onTextChange(ev, props.page)} />
            </Grid>
            <Grid item xs={12}>
                <LocalizedTextSetField disabled={disabled} variant="filled" size="small" label="Button" name="button" value={props.page.button} fullWidth onChange={(ev) => props.onTextChange(ev, props.page)} />
            </Grid>
        </Grid>
        <Grid item xs={12} container direction="row" spacing={2} sx={{ marginLeft: theme.spacing(2) }}>
           {props.page?.aspects?.length > 0 && !isCommentPage &&  <Grid item xs={12}>
                <Typography fontWeight={600}>Aspects</Typography>
            </Grid>}
            <DndProvider backend={HTML5Backend}>
                {(props.page?.aspects || []).map((aspect, idx) => (
                    <DraggableAspect
                        key={aspect.id}
                        aspect={aspect}
                        index={idx}
                        moveAspect={moveAspect}
                        disabled={disabled}
                    >
                        <Grid item xs={12} container direction="row" spacing={2} alignItems="center" pl={1} pt={0.25}>
                            {!isSmallScreen && !isCommentPage && (
                                <Grid item>
                                    <Tooltip title="Drag to reorder">
                                        <span>
                                            <IconButton size="small" disabled={disabled}>
                                                <DragIndicatorIcon color={disabled ? "disabled" : "primary"}/>
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Grid>
                            )}
                            <Grid item xs ml={isCommentPage ? 1 : 0} mt={isCommentPage ? 1.5 : 0} mb={1}>
                                <LocalizedTextSetField required disabled={disabled} variant="filled" size="small" label={isCommentPage ? 'Input text' : `Aspect ${idx+1}`} name="name" value={aspect.name} fullWidth onChange={(ev) => onAspectTextChange(ev, aspect, idx)} />
                            </Grid>
                            {/* <Grid item xs={12} sm={3} md={2}>
                                <FormControl fullWidth variant="filled" size="small">
                                    <InputLabel>Type</InputLabel>
                                    <Select
                                        name="type"
                                        value={aspect.type}
                                        label="Type"
                                        disabled={props?.page?.aspects?.filter(a => a.type === 0).length > 0}
                                        onChange={(ev) => onAspectSelectChange(ev, aspect, idx)}
                                        >
                                            { reviewAspectTypesMenuItems }
                                    </Select>
                                </FormControl>
                            </Grid> */}
                            {/* <Grid item xs={12} sm={3} md={2}>
                                { +aspect.type === REVIEW_ASPECT_TYPE_RATING && (
                                    <FormControl fullWidth variant="filled" size="small">
                                        <InputLabel>Show comment</InputLabel>
                                        <Select
                                            name="show_comment"
                                            value={aspect.show_comment}
                                            label="Show comment"
                                            disabled={true}
                                            onChange={(ev) => onAspectSelectChange(ev, aspect, idx)}
                                            >
                                                { reviewAspectCommentTypesMenuItems }
                                        </Select>
                                    </FormControl>
                                )}
                            </Grid> */}
                            {props.page.name !== "Comment page" && <Grid item mb={isSmallScreen ? 2 : 0}>
                                <Tooltip title={aspect.enabled === false ? 'Hidden from guests' : 'Shown to guests'}>
                                    <FormControlLabel
                                        disabled={disabled}
                                        control={<Switch name="enabled" checked={aspect.enabled !== false} onChange={(ev) => onAspectSwitchChange(ev, aspect, idx)}
                                            inputProps={{ 'aria-label': `Show aspect ${idx + 1}` }} />}
                                        label="Show"
                                    />
                                </Tooltip>
                            </Grid>}
                            {props.page.name !== "Comment page" && <Grid item mb={isSmallScreen ? 2 : 0}>
                                <DeleteIconButton title="Delete aspect" size="medium" fontSize="medium" disabled={disabled} onClick={(ev) => onDeleteAspect(ev, aspect, idx)} />
                            </Grid>}
                            { [REVIEW_ASPECT_COMMENT_TYPE_LOW_RATING, REVIEW_ASPECT_COMMENT_TYPE_HIGH_RATING, REVIEW_ASPECT_COMMENT_TYPE_ALWAYS].indexOf(+aspect.show_comment) >= 0 && (
                                <Grid item xs={12}>
                                    <LocalizedTextSetField disabled={disabled} variant="filled" size="small" label="Comment" name="comment_text" value={aspect.comment_text} fullWidth onChange={(ev) => onAspectTextChange(ev, aspect, idx)} />
                                </Grid>
                            )}
                        </Grid>
                    </DraggableAspect>
                ))}
            </DndProvider>
          {props.page?.name === "Thank you page" || (isCommentPage && props.page?.aspects?.length > 0) ? null : <Grid item xs={12} mb={1}>
                <Button disabled={disabled} variant="outlined" onClick={(ev) => props.onAddAspect(ev, props.page)}><AddIcon fontSize="small"/>&nbsp;Add Rate Aspect</Button>
            </Grid>}
        </Grid>
    </Grid>)
}


const BorderlessAccordion = styled(Accordion)(({ theme }) => ({
    border: 'none',
    '&:before': {
      display: 'none',
    },
    boxShadow: 'none',
  }));
  
const DEFAULT_PAGES = [
  { name: "Rate aspects", visibility: 1 },
  { name: "Comment page", visibility: 1 }, 
  { name: "Thank you page", visibility: 1 },
  { name: "Rate aspects", visibility: 2 },
  { name: "Comment page", visibility: 2 },
  { name: "Thank you page", visibility: 2 },
];

const AdvanecdReviewWidgetInner = (props) => {
    const [confirmDelete, confirmDeleteDialog] = useConfirmDialog();
    const dataAPI = React.useContext(DataAPIContext);
    const resource = 'review_page';
    const [pageHash, setPageHash] = React.useState({});
    const [pageChanged, setPageChanged] = React.useState({});
    const [positivePages, setPositivePages] = React.useState(null);
    const [negativePages, setNegativePages] = React.useState(null);
    const [hasEmptyFields, setHasEmptyFields] = React.useState({});
    const [loading, setLoading] = React.useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const { hasPerm } = React.useContext(ContextStore);
    const hasWritePerm = hasPerm('reviews', 'write');
    const hasDeletePerm = hasPerm('reviews', 'delete');

    React.useEffect(() => {
        if (positivePages !== null && negativePages !== null) { return; }
        let active = true;
        let ac = new AbortController();

        setLoading(true);

        (async () => {
            try {
                const params = {
                    signal: ac.signal,
                };
                const res = await dataAPI(GET_LIST, resource, params);
                if (!active) {
                    return;
                }
                if (res.statusCode === 200) {
                    const pp = [];
                    const np = [];
                    const ph = {};
                    
                    const existingPages = res.data;

                    // Filter out pages that don't match DEFAULT_PAGES
                    const validPages = existingPages.filter(page => 
                        DEFAULT_PAGES.some(dp => dp.name === page.name && dp.visibility === page.visibility)
                    );
                    if(hasWritePerm) { 
                        // Create missing default pages
                        for (const defaultPage of DEFAULT_PAGES) {
                            if (!validPages.some(page => page.name === defaultPage.name && page.visibility === defaultPage.visibility)) {
                                const createdPage = await dataAPI(CREATE, resource, { data: defaultPage });
                                validPages.push(createdPage.data);
                            }
                        }
                    }
                    if(hasDeletePerm) { 
                        // Delete extra pages
                        for (const page of existingPages) {
                            if (!DEFAULT_PAGES.some(dp => dp.name === page.name && dp.visibility === page.visibility)) {
                               if(page.id) {
                                await dataAPI(DELETE, resource, { id: page.id });
                               }
                            }
                        }
                    }

                    validPages.forEach(x => {
                        ph[x.id] = getComparableState(x);
                        if (x.visibility === 1) {
                            pp.push(x);
                        } else if (x.visibility === 2) {
                            np.push(x);
                        }
                    });
                    setPositivePages(pp);
                    setNegativePages(np);
                    setPageHash(ph);
                }
            } catch (e) {
                if (e.name !== 'AbortError') {
                    throw e;
                }
            } finally {
                setLoading(false);
            }
        })();

        return () => {
            active = false;
            ac.abort();
        }
    }, []);

    React.useEffect(() => {
        if (positivePages === null || negativePages === null || !hasWritePerm) { return; }

        const handleUpdatePage = async () => {
            const defaultNames = ["Rate aspects", "Comment page", "Thank you page"];
            const updateCommentPage = async (page) => {
                if (!page.aspects || page.aspects.length === 0) {
                    // Add a comment aspect if the page doesn't have any
                    const res = await dataAPI(CREATE, resource, { urlSuffix: `${page.id}/add_aspect/`, data: { type: REVIEW_ASPECT_TYPE_COMMENT } });
                    if (res.statusCode === 201) {
                        page.aspects.push(res.data.aspects[res.data.aspects.length - 1]);
                    }
                } 
                page.aspects.forEach(a => a.type = REVIEW_ASPECT_TYPE_COMMENT);
                page.sequence = 2;
                await onSavePage('ev', page, true);
            };
            for (const x of positivePages) {
                if (!defaultNames.includes(x.name) && hasDeletePerm) {
                    await onDeletePage('ev', x, true);
                    continue;
                }
                if (x.name === 'Rate aspects' && ((x.aspects.length > 0 && x.aspects.filter(a => a.type === REVIEW_ASPECT_TYPE_COMMENT).length > 0) || x.sequence !== 1)) {
                    x.aspects.forEach(a => a.type = REVIEW_ASPECT_TYPE_RATING);
                    x.sequence = 1;
                    await onSavePage('ev', x, true);
                }
                if (x.name === 'Comment page' && ((!x.aspects|| x.aspects.length === 0 ) || (x.aspects.length > 0 && x.aspects.filter(a => a.type === REVIEW_ASPECT_TYPE_RATING).length > 0) || x.sequence !== 2)) {
                    await updateCommentPage(x);
                }
                if (x.name === 'Thank you page' && (!x.enabled || x.sequence !== 3)) {
                    x.enabled = true;
                    x.sequence = 3;
                    await onSavePage('ev', x, true);
                }
            }

            for (const x of negativePages) {
                if (!defaultNames.includes(x.name) && hasDeletePerm) {
                    await onDeletePage('ev', x ,true);
                    continue;
                }
                if (x.name === 'Rate aspects' && ((x.aspects.length > 0 && x.aspects.filter(a => a.type === REVIEW_ASPECT_TYPE_COMMENT).length > 0) || x.sequence !== 1)) {
                    x.aspects.forEach(a => a.type = REVIEW_ASPECT_TYPE_RATING);
                    x.sequence = 1;
                    await onSavePage('ev', x, true);
                }
                if (x.name === 'Comment page' && ((!x.aspects|| x.aspects.length === 0 ) || (x.aspects.length > 0 && x.aspects.filter(a => a.type === REVIEW_ASPECT_TYPE_RATING).length > 0) || x.sequence !== 2)) {
                    await updateCommentPage(x);
                }
                if (x.name === 'Thank you page' && (!x.enabled || x.sequence !== 3)) {
                    x.enabled = true;
                    x.sequence = 3;
                    await onSavePage('ev', x,true);
            }
        };}

        handleUpdatePage();
    }, [positivePages, negativePages]);


    const onDeletePage = async (ev, page, force = false) => {
        if(!page?.id){
            return;
        }
        if (force || (await confirmDelete({ body: `Are you sure you want to delete the page "${page.name}" (${page.id})?` }))) {
            await dataAPI(DELETE, resource, { id: page.id }).then((res) => {
                enqueueSnackbar('Page deleted', { variant: 'success' });
                setPositivePages((prev) => prev.filter(x => x.id !== page.id));
                setNegativePages((prev) => prev.filter(x => x.id !== page.id));
            })
        }
    };

    const isObjectEmpty = (objectName) => {
        for (let prop in objectName) {
          if (objectName.hasOwnProperty(prop) && objectName[prop] !== '') {
            return false;
          }
        }
        return true;
    };

    const aspectsHasEmptyField = (aspects) => {
        var found = aspects.filter(
            function(item) {
                return (isObjectEmpty(item.name.values));
        })
        return found.length > 0;
    };

    const updatePage = (page) => {
        const hash = getComparableState(page);
        setPageChanged(prev => ({ ...prev, [page.id]: hash !== pageHash[page.id] }));
        setHasEmptyFields(prev => ({ ...prev, [page.id]: aspectsHasEmptyField(page.aspects) }));
        if (!page.visibility || page.visibility === 1) {
            setPositivePages((prev) => prev.map(x => x.id === page.id ? page : x));
        }
        if (!page.visibility || page.visibility === 2) {
            setNegativePages((prev) => prev.map(x => x.id === page.id ? page : x));
        }
    }

    const onSavePage = async (ev, page, silent = false) => {
        await dataAPI(UPDATE, resource, { id: page.id, data: page }).then((res) => {
            if (res.statusCode === 200) {
                setPageHash(prev => ({ ...prev, [page.id]: getComparableState(page) }));
                setPageChanged(prev => ({ ...prev, [page.id]: false }));
                setHasEmptyFields(prev => ({ ...prev, [page.id]: false }));
                if(!silent){enqueueSnackbar('Page saved', { variant: 'success' });}
            } else {
                if(!silent){enqueueSnackbar(`Error saving page (${res.statusCode})`, { variant: 'error' });}
            }
        });
    }

    const onPageTextChange = (ev, page) => {
        page[ev.target.name] = ev.target.value;
        updatePage(page);
    };

    const onPageSwitchChange = (ev, page) => {
        page[ev.target.name] = !!ev.target.checked;
        updatePage(page);
    }

    const onAddAspect = (ev, page) => {

        const aspectType = page.name === "Comment page" ? 3 : REVIEW_ASPECT_TYPE_RATING;

        dataAPI(CREATE, resource, { urlSuffix: `${page.id}/add_aspect/`, data: { type: aspectType } }).then((res) => {
            if (res.statusCode === 201) {
                page.aspects.push(res.data.aspects[res.data.aspects.length - 1])
                updatePage(page);
            } else {
                enqueueSnackbar(`Error adding aspect (${res.statusCode})`, { variant: 'error' });
            }
        });
    }

    return (<>
        {confirmDeleteDialog}
        <Grid container direction="row" spacing={2}>
            <FixedLinearProgress visibility={loading} />
            <Grid item xs={12}>
                <BorderlessAccordion style={{border: 'solid 1px #D8D8D8', backgroundColor: 'white', borderRadius: '12px'}}>
                    <AccordionSummary expandIcon={<ArrowDropDownIcon />}>
                        <Chip label="Positive feedback"  style={{backgroundColor: '#DAF3E5'}}/>
                    </AccordionSummary>
                    <AccordionDetails >
                        <Grid container direction="row" spacing={2} >
                            { (positivePages || []).toSorted(sortBySequence).map((page, idx, lst) => (<Grid item xs={12} key={page.id}>
                                <ReviewWidgetPage
                                    page={page}
                                    positive={true}
                                    onDelete={onDeletePage}
                                    onSave={onSavePage}
                                    onTextChange={onPageTextChange}
                                    onSwitchChange={onPageSwitchChange}
                                    onAddAspect={onAddAspect}
                                    onUpdatePage={updatePage}
                                    pageChanged={pageChanged[page.id]}
                                    disabled={!hasWritePerm}
                                    hasEmptyFields={hasEmptyFields[page.id]}
                                />
                                { idx < lst.length - 1 && <hr /> }
                            </Grid>)) }
                        </Grid>
                    </AccordionDetails>
                </BorderlessAccordion>
            </Grid>
            <Grid item xs={12}>
                <BorderlessAccordion style={{border: 'solid 1px #D8D8D8', backgroundColor: 'white', borderRadius: '12px'}}>
                    <AccordionSummary expandIcon={<ArrowDropDownIcon />}>
                        <Chip label="Negative feedback"  style={{backgroundColor: '#F8D7DA'}}/>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Grid container direction="row" spacing={2} >
                            { (negativePages || []).toSorted(sortBySequence).map((page, idx, lst) => (<Grid item xs={12} key={page.id}>
                                <ReviewWidgetPage
                                    page={page}
                                    positive={false}
                                    onDelete={onDeletePage}
                                    onSave={onSavePage}
                                    onTextChange={onPageTextChange}
                                    onSwitchChange={onPageSwitchChange}
                                    onAddAspect={onAddAspect}
                                    onUpdatePage={updatePage}
                                    pageChanged={pageChanged[page.id]}
                                    disabled={!hasWritePerm}
                                    hasEmptyFields={hasEmptyFields[page.id]}
                                />
                                { idx < lst.length - 1 && <hr /> }
                            </Grid>)) }
                        </Grid>
                    </AccordionDetails>
                </BorderlessAccordion>
            </Grid>
        </Grid>
    </>)
};

const AdvanecdReviewWidget = React.memo(AdvanecdReviewWidgetInner);
export { AdvanecdReviewWidget };
export default AdvanecdReviewWidget;
